import { type TileBitmapCache } from "./tileBitmapCache";
import { type NormalizedRect, pickDrawLevel, type TiledDrawingSource, tileDocRect, visibleTileRange } from "./tileMath";

/**
 * Destination rectangle (in CSS px, canvas coordinate space) that the whole
 * document maps onto. Same shape as `calculateImageFitBox`'s result.
 */
export type FitBox = { x: number; y: number; width: number; height: number };

/**
 * Paint the tiled drawing into `fitBox`, fetching only what the viewport
 * needs: level via `pickDrawLevel` (sharp but budget-clamped), only tiles
 * intersecting the visible part of `fitBox`, with `baseBitmap` (the medium
 * preview) underneath so still-loading areas show blurry content instead of
 * holes. Missing tiles are requested through the cache, whose `onTileLoaded`
 * triggers the owner's repaint. `ctx` is in CSS pixels; `dpr` only affects
 * level selection.
 */
export function drawTiledImage({
    ctx,
    source,
    cache,
    baseBitmap,
    fitBox,
    viewportWidth,
    viewportHeight,
    dpr,
    tileBudget,
}: {
    ctx: CanvasRenderingContext2D;
    source: TiledDrawingSource;
    cache: TileBitmapCache;
    /** Preview bitmap painted under the tiles (skipped when unavailable). */
    baseBitmap: ImageBitmap | undefined;
    fitBox: FitBox;
    viewportWidth: number;
    viewportHeight: number;
    dpr: number;
    /** Hard cap on tiles one paint may touch; keep below the cache's `maxTiles`. */
    tileBudget: number;
}): void {
    // Marks the tiles this pass is about to request as the current viewport,
    // so a stale load from an earlier pass (e.g. a viewport the user has
    // since panned/zoomed away from) can drop itself instead of evicting one
    // of them from the cache's LRU -- see `TileBitmapCache.beginFrame`.
    cache.beginFrame();

    // Visible part of the document, normalized to 0..1 of the fit box.
    const region: NormalizedRect = {
        x0: clamp01((0 - fitBox.x) / fitBox.width),
        y0: clamp01((0 - fitBox.y) / fitBox.height),
        x1: clamp01((viewportWidth - fitBox.x) / fitBox.width),
        y1: clamp01((viewportHeight - fitBox.y) / fitBox.height),
    };
    if (region.x1 <= region.x0 || region.y1 <= region.y0) {
        return;
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    if (baseBitmap && baseBitmap.width > 0) {
        ctx.drawImage(baseBitmap, fitBox.x, fitBox.y, fitBox.width, fitBox.height);
    }

    const targetPx = Math.max(fitBox.width, fitBox.height) * dpr;
    const level = pickDrawLevel(source, targetPx, region, tileBudget);
    drawLevel(ctx, source, cache, fitBox, level, region, tileBudget);
}

function drawLevel(
    ctx: CanvasRenderingContext2D,
    source: TiledDrawingSource,
    cache: TileBitmapCache,
    fitBox: FitBox,
    level: number,
    region: NormalizedRect,
    tileBudget: number,
): void {
    const { txMin, txMax, tyMin, tyMax } = visibleTileRange(source, level, region);

    // `pickDrawLevel`'s clamp is best-effort (it bottoms out at `minLevel`);
    // this is the hard cap that keeps one paint from thrashing the cache. Not
    // painting a tile is safe — the base preview covers the remainder.
    let budgetLeft = tileBudget;
    for (let ty = tyMin; ty <= tyMax && budgetLeft > 0; ty++) {
        for (let tx = txMin; tx <= txMax && budgetLeft > 0; tx++) {
            budgetLeft--;
            const bitmap = cache.getOrRequest(level, tx, ty);
            if (!bitmap) {
                continue;
            }
            const rect = tileDocRect(source, level, tx, ty);
            // Derive both edges from absolute fit-box coordinates so adjacent
            // tiles share edges exactly (no rounding seams).
            const dx0 = fitBox.x + rect.x0 * fitBox.width;
            const dx1 = fitBox.x + rect.x1 * fitBox.width;
            const dy0 = fitBox.y + rect.y0 * fitBox.height;
            const dy1 = fitBox.y + rect.y1 * fitBox.height;
            ctx.drawImage(bitmap, dx0, dy0, dx1 - dx0, dy1 - dy0);
        }
    }
}

function clamp01(value: number): number {
    return Math.min(1, Math.max(0, value));
}
